import React, { useEffect, useMemo, useState } from 'react';
import { studentApi } from '../../services/api';
import { emitCreditsChanged } from '../../utils/credits';
import FilterPills from '../../components/FilterPills';

const F = { fontFamily: 'Inter' } as const;

interface Slot {
  id: string;
  institution_name: string;
  start_at: string;
  end_at: string;
  capacity: number;
  reserved_count: number;
  credit_cost: number;
  assigned_teacher_name?: string | null;
  my_status: 'booked' | null;
}

// Saatler okul saatidir (TR, UTC+3) — cihaz saat dilimi ne olursa olsun TR
// duvar saati gösterilir (display-only sabit ofset, fmtDeadline ile aynı kalıp).
const TR_OFFSET = 3 * 3600 * 1000;
const trDate = (iso: string) => new Date(new Date(iso).getTime() + TR_OFFSET);
const trTime = (iso: string) => {
  const d = trDate(iso);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};
const DAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const trDayLabel = (iso: string) => {
  const d = trDate(iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${DAYS[d.getUTCDay()]}`;
};

// Gün / Hafta (Pzt–Paz) / Ay penceresi — admin panelindekiyle aynı LibCal
// kalıbı; tüm slotları tek listede dökmek yerine öğrenci pencereler arasında
// gezinir.
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
type WindowView = 'day' | 'week' | 'month';
const VIEW_LABEL: Record<WindowView, string> = { day: 'Gün', week: 'Hafta', month: 'Ay' };
function windowRange(view: WindowView, anchor: Date): { from: Date; to: Date } {
  if (view === 'day') {
    const d = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    return { from: d, to: d };
  }
  if (view === 'month') {
    return {
      from: new Date(anchor.getFullYear(), anchor.getMonth(), 1),
      to: new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0),
    };
  }
  const dow = anchor.getDay() === 0 ? 6 : anchor.getDay() - 1;
  const from = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - dow);
  const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6);
  return { from, to };
}

const LibraryReservations: React.FC = () => {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [myBookings, setMyBookings] = useState<Slot[]>([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirmSlot, setConfirmSlot] = useState<Slot | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [view, setView] = useState<WindowView>('week');
  const [anchor, setAnchor] = useState(() => new Date());

  const range = useMemo(() => windowRange(view, anchor), [view, anchor]);
  const rangeLabel = view === 'month'
    ? `${MONTHS[range.from.getMonth()]} ${range.from.getFullYear()}`
    : view === 'day'
      ? `${range.from.getDate()} ${MONTHS[range.from.getMonth()]} ${DAYS[range.from.getDay()]}`
      : `${range.from.getDate()} ${MONTHS[range.from.getMonth()]} – ${range.to.getDate()} ${MONTHS[range.to.getMonth()]}`;
  const shift = (dir: -1 | 1) => setAnchor((a) => view === 'month'
    ? new Date(a.getFullYear(), a.getMonth() + dir, 1)
    : new Date(a.getFullYear(), a.getMonth(), a.getDate() + dir * (view === 'day' ? 1 : 7)));

  // silent=true → background poll: don't flash the loading spinner or surface a
  // transient fetch error over the already-rendered grid.
  const load = (silent = false) => {
    if (!silent) setLoading(true);
    studentApi.librarySlots(ymd(range.from), ymd(range.to))
      .then((res) => {
        setSlots(res.data.slots ?? []);
        setMyBookings(res.data.my_bookings ?? []);
        setBalance(res.data.balance ?? 0);
      })
      .catch(() => { if (!silent) setErr('Kütüphane saatleri yüklenemedi.'); })
      .finally(() => { if (!silent) setLoading(false); });
  };
  useEffect(() => { load(); }, [view, anchor]); // eslint-disable-line react-hooks/exhaustive-deps

  // B20: slot picking needs near-real-time availability — poll the current
  // window every 15s so seats another student grabs/frees show up. Silent
  // (no spinner), paused when the tab is hidden, re-armed when the window
  // (view/anchor) changes so it always fetches the range on screen.
  useEffect(() => {
    const POLL_MS = 15_000;
    const timer = setInterval(() => { if (!document.hidden) load(true); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) load(true); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [view, anchor]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const grouped = useMemo(() => {
    const m = new Map<string, Slot[]>();
    for (const s of slots) {
      const day = trDayLabel(s.start_at);
      if (!m.has(day)) m.set(day, []);
      m.get(day)!.push(s);
    }
    return Array.from(m.entries());
  }, [slots]);

  const book = async (slot: Slot) => {
    setBusy(true); setErr(null);
    try {
      const res = await studentApi.bookLibrarySlot(slot.id);
      setBalance(res.data.new_balance);
      emitCreditsChanged(res.data.new_balance); // instant TopBar coin update
      setConfirmSlot(null);
      showToast('Rezervasyon alındı! 🎉');
      load();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? 'Rezervasyon yapılamadı.');
    } finally { setBusy(false); }
  };

  const cancel = async (slot: Slot) => {
    if (!window.confirm(`${trDayLabel(slot.start_at)} ${trTime(slot.start_at)}–${trTime(slot.end_at)} rezervasyonun iptal edilsin mi? ${slot.credit_cost > 0 ? `${slot.credit_cost} kredi iade edilir.` : ''}`)) return;
    setBusy(true); setErr(null);
    try {
      const res = await studentApi.cancelLibraryBooking(slot.id);
      setBalance(res.data.new_balance);
      emitCreditsChanged(res.data.new_balance); // instant TopBar coin update
      showToast('Rezervasyon iptal edildi, LMS Coin iade edildi.');
      load();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? 'İptal edilemedi.');
    } finally { setBusy(false); }
  };

  return (
    <div>
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#3973bf] text-white text-[14px] font-semibold px-5 py-2.5 rounded-[12px] shadow-lg">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Kütüphane Rezervasyon</h1>
          <p style={{ ...F, fontSize: 14, fontWeight: 400, color: '#888' }}>
            Kurumunun açtığı kütüphane saatlerinden yerini ayırt, LMS Coin ile rezerve et. Bir gün için en fazla 3 kütüphane rezervasyonu yapabilirsin.
          </p>
        </div>
        <div
          className="flex items-center gap-2 px-4 py-2.5 rounded-[16px] bg-[#fafafa]"
          style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
        >
          <img src="/new-icons/LMS 1.svg" alt="" className="w-[24px] h-[24px] object-contain" />
          <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#3973bf' }}>{balance}</span>
          <span style={{ ...F, fontSize: 13, fontWeight: 500, color: '#888' }}>LMS Coin</span>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-[14px] rounded-[12px] px-4 py-3 mb-4" style={F}>
          {err}
        </div>
      )}

      {/* Rezervasyonlarım */}
      {myBookings.length > 0 && (
        <div className="mb-5">
          <h2 style={{ ...F, fontSize: 18, fontWeight: 600, color: '#2b2b2b' }} className="mb-2">Rezervasyonlarım</h2>
          <div className="flex gap-3 flex-wrap">
            {myBookings.map((s) => (
              <div
                key={s.id}
                className="bg-[#fafafa] rounded-[16px] px-4 py-3 flex items-center gap-4 border-l-4 border-[#59e56e]"
                style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
              >
                <div>
                  <p style={{ ...F, fontSize: 14, fontWeight: 700, color: '#2b2b2b' }}>{trDayLabel(s.start_at)}</p>
                  <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#3973bf' }}>{trTime(s.start_at)} – {trTime(s.end_at)}</p>
                  {s.assigned_teacher_name && (
                    <p style={{ ...F, fontSize: 12, fontWeight: 400, color: '#888' }}>Sorumlu: {s.assigned_teacher_name}</p>
                  )}
                </div>
                <button
                  onClick={() => cancel(s)}
                  disabled={busy}
                  className="text-[13px] font-semibold text-[#d40000] bg-red-50 hover:bg-red-100 rounded-[10px] px-3 py-2 transition-colors disabled:opacity-50"
                  style={F}
                >
                  İptal Et
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hafta/Ay gezgini */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <button onClick={() => shift(-1)} disabled={busy} className="bg-[#fafafa] rounded-[10px] w-9 h-9 text-[18px] text-[#2b2b2b] hover:bg-gray-100" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }} aria-label="Önceki">‹</button>
        <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#2b2b2b' }} className="min-w-[170px] text-center">{rangeLabel}</span>
        <button onClick={() => shift(1)} disabled={busy} className="bg-[#fafafa] rounded-[10px] w-9 h-9 text-[18px] text-[#2b2b2b] hover:bg-gray-100" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }} aria-label="Sonraki">›</button>
        <button onClick={() => setAnchor(new Date())} className="bg-[#fafafa] rounded-[10px] px-3 h-9 text-[13px] font-semibold text-[#2b2b2b] hover:bg-gray-100" style={{ ...F, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>Bugün</button>
        <FilterPills
          tone="blue"
          className="ml-1"
          value={view}
          onChange={setView}
          options={(['day', 'week', 'month'] as const).map((v) => ({ value: v, label: VIEW_LABEL[v] }))}
        />
      </div>

      {/* Müsait saatler — gün gün */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : slots.length === 0 ? (
        <div className="bg-[#fafafa] rounded-[24px] p-10 text-center" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#888' }}>
            {rangeLabel} aralığında açık kütüphane saati yok. ‹ › tuşları ile diğer tarihlere bakabilirsin.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([day, daySlots]) => (
            <div key={day} className="bg-[#fafafa] rounded-[24px] p-4 xl:p-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <p style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }} className="mb-3">{day}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {daySlots.map((s) => {
                  const left = s.capacity - s.reserved_count;
                  const full = left <= 0;
                  const mine = s.my_status === 'booked';
                  const cantAfford = !mine && !full && balance < s.credit_cost;
                  return (
                    <div
                      key={s.id}
                      className={`rounded-[14px] p-3 border-2 ${mine ? 'border-[#59e56e] bg-green-50/40' : full ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-[#e6e9ef] bg-white'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#2b2b2b' }}>
                          {trTime(s.start_at)} – {trTime(s.end_at)}
                        </span>
                        <span className="flex items-center gap-1 text-[12px] font-bold text-[#3973bf]" style={F}>
                          <img src="/new-icons/LMS 1.svg" alt="" className="w-[16px] h-[16px] object-contain" />
                          {s.credit_cost}
                        </span>
                      </div>
                      <p style={{ ...F, fontSize: 12, fontWeight: 500, color: '#888' }} className="mb-1">
                        {s.assigned_teacher_name ? `Sorumlu: ${s.assigned_teacher_name}` : 'Sorumlu atanmadı'}
                      </p>
                      <p style={{ ...F, fontSize: 12, fontWeight: 600, color: full ? '#d40000' : '#59a05e' }} className="mb-2">
                        {full ? 'Dolu' : `${left} yer kaldı`}
                      </p>
                      {mine ? (
                        <button
                          onClick={() => cancel(s)}
                          disabled={busy}
                          className="w-full text-[13px] font-semibold text-white bg-[#59e56e] rounded-[10px] py-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                          style={F}
                        >
                          Rezervasyonun Var: İptal Et
                        </button>
                      ) : (
                        <button
                          onClick={() => { setErr(null); setConfirmSlot(s); }}
                          disabled={busy || full || cantAfford}
                          className={`w-full text-[13px] font-semibold rounded-[10px] py-2 transition-opacity ${
                            full || cantAfford
                              ? 'bg-[#c5c5c5] text-white cursor-not-allowed'
                              : 'bg-[#3973bf] text-white hover:opacity-90'
                          }`}
                          style={F}
                        >
                          {full ? 'Dolu' : cantAfford ? 'Yetersiz LMS Coin' : 'Rezerve Et'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Onay modalı */}
      {confirmSlot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => !busy && setConfirmSlot(null)} />
          <div className="relative bg-[#f7f7f7] rounded-[24px] w-full max-w-[440px] p-7" style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.15)' }}>
            <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#2b2b2b' }} className="mb-3">Rezervasyonu Onayla</h2>
            <div className="bg-white rounded-[14px] p-4 mb-4 space-y-1">
              <p style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>{trDayLabel(confirmSlot.start_at)}</p>
              <p style={{ ...F, fontSize: 15, fontWeight: 500, color: '#3973bf' }}>{trTime(confirmSlot.start_at)} – {trTime(confirmSlot.end_at)}</p>
              {confirmSlot.assigned_teacher_name && (
                <p style={{ ...F, fontSize: 13, fontWeight: 400, color: '#888' }}>Sorumlu: {confirmSlot.assigned_teacher_name}</p>
              )}
              <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#2b2b2b' }}>
                Maliyet: <strong>{confirmSlot.credit_cost} kredi</strong> · Kalan bakiyen: <strong>{balance - confirmSlot.credit_cost}</strong>
              </p>
            </div>
            {err && <p style={{ ...F, fontSize: 13 }} className="text-red-600 mb-3">{err}</p>}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmSlot(null)}
                disabled={busy}
                className="text-[14px] font-semibold text-[#2b2b2b] bg-white border-2 border-gray-200 rounded-[12px] px-5 py-2.5 hover:bg-gray-50 transition-colors"
                style={F}
              >
                Vazgeç
              </button>
              <button
                onClick={() => book(confirmSlot)}
                disabled={busy}
                className="text-[14px] font-semibold text-white bg-[#3973bf] rounded-[12px] px-5 py-2.5 hover:opacity-90 transition-opacity disabled:opacity-50"
                style={F}
              >
                {busy ? 'Rezerve ediliyor…' : 'Rezerve Et'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryReservations;
